import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HippoUtilsService } from '@hippo/services/utils';
import {
    catchError,
    from,
    lastValueFrom,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { clearEventProgressFilter } from 'app/modules/admin/events/pages/event-progress/event-progress.service';
import { UserService } from '../services/user.service';
import { clearOpenedMenus } from '../navigation/menu-state';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;

    constructor(
        private _httpClient: HttpClient,
        private userService: UserService
    ) {}

    resetPassword(
        initials: string,
        password: string,
        language: string = 'de'
    ): Observable<any> {
        const formData = new FormData();
        formData.append('submit_option', 'opt_new_login');
        formData.append('submit_iso_code', language);
        formData.append('initials', initials);
        formData.append('password', password);
        formData.append('password_repeat', password);

        const uid = this.userService?.user?.uid;
        if (!uid) {
            return throwError(
                () =>
                    new Error('Cannot reset password: user session not found.')
            );
        }
        return this._httpClient
            .post(
                `hippo/navigator.php?page=pages/common/Login.php&page_type=json&uid=${uid}`,
                formData
            )
            .pipe(
                switchMap((response: any) => {
                    if (response.url) {
                        const urlParams = new URLSearchParams(response.url);
                        const uid = urlParams.get('uid');
                        if (uid) {
                            localStorage.setItem('uid', uid);
                        }
                    }

                    this._authenticated = true;
                    return of(response);
                })
            );
    }

    signIn(
        email: string,
        password: string,
        language: string = 'de'
    ): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        const formData = new FormData();
        formData.append('submit_option', 'opt_login');
        formData.append('submit_iso_code', language);
        formData.append('initials', email);
        formData.append('password', password);

        return this.withSession(() =>
            this._httpClient.post(
                'hippo/navigator.php?page=pages/common/Login.php&page_type=json',
                formData
            )
        );
    }

    signInWithCognitoToken(idToken: string): Observable<any> {
        const formData = new FormData();
        formData.append('id_token', idToken);

        return this.withSession(() =>
            this._httpClient.post(
                'hippo/navigator.php?page=pages/common/CognitoLogin.php&page_type=json',
                formData
            )
        );
    }

    /**
     * The PHP 8 backend starts the session with the login request itself. The
     * PHP 7 backend does not send a cookie there, so only in that case the
     * session page is fetched once and the login repeated. A new login starts
     * with the menu groups from the profile settings again.
     */
    private withSession(login: () => Observable<any>): Observable<any> {
        return login().pipe(
            switchMap(response =>
                response?.status !== false &&
                !HippoUtilsService.getCookie('PHPSESSID')
                    ? from(this.getPhpSessionCookie()).pipe(
                          switchMap(() => login())
                      )
                    : of(response)
            ),
            tap(response => {
                if (response?.status && response?.result?.id) {
                    clearOpenedMenus(String(response.result.id));
                }
            })
        );
    }

    /**
     * Ends the session of *this* tab.
     *
     * Besides dropping the tab-local user it also tells PHP to throw away the
     * session data of this tab's uid, which used to be left behind completely
     * — the Angular app never called the logout endpoint, so privileges,
     * settings and user data survived in `$_SESSION` until the nightly
     * cleanup swept them (HIP-62).
     *
     * `$_SESSION` is keyed by uid, so this only removes this tab's own
     * section: a tab logged in as another user keeps its valid session.
     */
    signOut(): Observable<any> {
        const uid =
            this.userService.getUser()?.uid ?? this.userService.user?.uid;

        sessionStorage.removeItem('user');
        // Page filters remembered for this tab belong to the session that is
        // ending — the next login here must not inherit them (HIP-64).
        clearEventProgressFilter();
        this._authenticated = false;

        if (!uid) {
            return of(true);
        }

        return this._httpClient
            .get(
                `hippo/navigator.php?page=pages/common/Logout.php&uid=${encodeURIComponent(uid)}&page_type=json`
            )
            .pipe(
                // A session that is already gone on the server ("user is
                // already logged out") must not keep the user on the page.
                catchError(() => of(true))
            );
    }

    check(): Observable<boolean> {
        let user = this.userService.getUser();
        if (user?.uid) {
            this._authenticated = true;
            return of(true);
        }
        this._authenticated = false;
        return of(false);
    }

    /**
     * Makes sure this browser has a PHP session cookie to log in against.
     *
     * It deliberately never throws an existing PHPSESSID away: the cookie is
     * shared by every tab and dropping it orphaned the sessions of all the
     * other tabs at once, so logging in as a second user in a new tab kicked
     * the first one out (HIP-62). A fresh session is not needed either —
     * every login allocates its own uid inside `$_SESSION`, and signing out
     * now removes the previous uid's data server-side.
     */
    async getPhpSessionCookie() {
        const phpSessionId = HippoUtilsService.getCookie('PHPSESSID');
        if (phpSessionId) {
            return;
        }
        try {
            await lastValueFrom(
                this._httpClient.get('hippo/', { responseType: 'text' })
            );
        } catch (error) {}
    }
}
